/**
 * page-tabs.js — the page switcher in row 1.
 *
 * Click to switch, double-click or F2 to rename, right-click / Shift+F10 for
 * the context menu, and drag or Alt+←/→ to reorder. Enter commits a rename;
 * Escape and blur abandon it. FN-3 patches tabs by page id so state changes
 * preserve focused nodes, rename drafts and selections. Dirty dots and roving
 * tabindex follow the same retained nodes.
 *
 * Two things DO change:
 *
 *  1. **Construction goes through the kit.** The old code hand-created every
 *     `<button>`, which `no-hand-rolled-button` exists to stop. "Port verbatim"
 *     was not available.
 *
 *  2. **Adaptive collapse replaces the scroll-fade.** Past the point where the
 *     tabs no longer fit, the strip becomes a single `Home ▾ · 8 pages` trigger
 *     opening the same list as a popover. A horizontally scrolling strip of 12
 *     tabs is a hunt; a list is not.
 *
 * ── Why collapse cannot be measured from the rendered strip ───────────────
 * The obvious implementation asks "does the strip overflow?" and collapses if
 * so. That oscillates: collapsing makes the strip narrow, which makes it fit,
 * which expands it, which makes it overflow. The measurement has to be
 * independent of the current mode.
 *
 * So the natural width comes from an OFFSCREEN PROXY — a hidden copy of the
 * strip that is always laid out at full width and never collapses. `decideMode`
 * is then a pure function of (naturalWidth, available, currentMode), with a
 * hysteresis band on top for sub-pixel jitter.
 *
 * Prototyped in Chromium before being written (the plan required it): sweeping
 * 1400→200→1400px in 1px steps with two settle passes at each width produced
 * exactly TWO mode flips — collapse at 1041px, expand at 1066px — and zero
 * widths that flipped more than once. Pinned by `command-bar-geometry.mjs`.
 */

import { eventBus } from '../../core/events.js';
import { store, readOnlyReasonFor } from '../../core/state.js';
import { createButton } from '../components.js';
import { showToast } from '../toast.js';
import { icon } from '../icons.js';
import { openPopoverMenu } from '../popover-menu.js';
import { NESTED_ATTR } from './focus-model.js';
import { isComposing } from '../../shared/typing-target.js';

import { PAGE_RENAME_MAX_LEN, showRenamePageDialog } from './rename-page-dialog.js';
import { openPageActions } from '../page-actions.js';
import { isHiddenOrInert } from '../tabbable.js';
import { isPhoneViewport } from '../../shared/viewport-service.js';
import { movePageId } from './page-order.js';

/**
 * The expand threshold sits this far ABOVE the collapse threshold. Belt and
 * braces: the offscreen proxy already removes the feedback loop, so this only
 * absorbs sub-pixel layout jitter and a scrollbar appearing.
 */
export const COLLAPSE_HYSTERESIS_PX = 24;

/**
 * The strip scrolls, so it no longer needs its NATURAL width to stay usable —
 * it needs enough room to show a tab and the edge fade the rest scrolls behind.
 * Collapsing to the "N pages" button is therefore a last resort for a genuinely
 * tiny bar, not the ordinary answer to "more pages than fit".
 *
 * Required width stays the SMALLER of natural and this floor, so a project with
 * few pages behaves exactly as it did before: it never collapses while its tabs
 * already fit.
 */
export const MIN_SCROLLABLE_PX = 180;

/**
 * Pure. Exported so the decision can be tested without a browser, and so the
 * geometry gate and the unit test agree on the same function.
 *
 * @param {number} naturalWidth — the strip's width when NOT collapsed, measured
 *   from the offscreen proxy so it never depends on the current mode.
 * @param {number} available — the space the strip may occupy.
 * @param {'tabs'|'collapsed'} currentMode
 * @returns {'tabs'|'collapsed'}
 */
export function decideMode(naturalWidth, available, currentMode) {
  if (!Number.isFinite(naturalWidth) || !Number.isFinite(available)) return currentMode;
  if (currentMode === 'tabs') return naturalWidth > available ? 'collapsed' : 'tabs';
  return naturalWidth + COLLAPSE_HYSTERESIS_PX <= available ? 'tabs' : 'collapsed';
}

export class PageTabs {
  /** @param {HTMLElement} container */
  constructor(container) {
    this._container = container;
    this._mode = 'tabs';
    this._dirtyPageIds = new Set();
    this._pageStateHashes = new Map();
    this._hasBaseline = false;
    this._dragPageId = null;
    this._renameSession = null;
    this._renderCount = 0;
    this._projectGeneration = store.getEpoch() - store.getRevision();
    this._projectReplaced = false;
    this._offs = [];
    this._observer = null;
    this._frame = null;
    this._render();
    this._listen();
  }

  _render() {
    this._container.classList.add('pb-cb-pages');

    this._strip = document.createElement('div');
    // The id moves WITH the control, same rule as #pb-undo-btn in CMD-2:
    // `ui/tour/tour-registry.js` walks the user to `#pb-page-tabs` in two
    // steps, and re-homing the strip is not deleting it. The census parity gate
    // catches the id vanishing, which is how this was found.
    this._strip.id = 'pb-page-tabs';
    this._strip.className = 'pb-cb-pages-strip';
    this._strip.setAttribute('role', 'tablist');
    this._strip.setAttribute('aria-label', 'Pages');
    // The strip owns its own arrow keys (roving tabindex across the tabs) and
    // is therefore ONE stop to the row's composite focus model — contracts §3,
    // implemented in `focus-model.js` (CMD-8).
    this._strip.setAttribute(NESTED_ATTR, 'page-tabs');

    // The measurement proxy. `aria-hidden` + `inert`-equivalent: it must never
    // be reachable by a screen reader or the tab order, or every page would be
    // announced twice.
    this._proxy = document.createElement('div');
    this._proxy.className = 'pb-cb-pages-proxy';
    this._proxy.setAttribute('aria-hidden', 'true');

    this._collapsed = createButton({
      variant: 'bare',
      className: 'pb-cb-pages-collapsed',
      ariaHaspopup: 'menu',
      onClick: (e) => this._openPageList(e.currentTarget)
    });
    this._collapsed.hidden = true;

    this._addBtn = createButton({
      variant: 'bare',
      className: 'pb-cb-ptab-add',
      icon: icon('plus', { size: 'xs' }),
      ariaLabel: 'Add page',
      tooltip: 'Add page',
      tooltipShortcut: 'Ctrl+N',
      onClick: () => eventBus.emit('toolbar:add-page')
    });
    this._addBtn.tabIndex = -1;
    this._strip.appendChild(this._addBtn);

    // BC-9: page actions get a VISIBLE home. Rename was double-click/F2,
    // duplicate and delete a right-click, reorder a drag — gestures nobody
    // finds. The button sits OUTSIDE the scrolling strip so it is never
    // scrolled away, and acts on the page you are on.
    this._actionsBtn = createButton({
      variant: 'bare',
      className: 'pb-cb-ptab-actions',
      icon: icon('more', { size: 'xs' }),
      ariaLabel: 'Page actions',
      tooltip: 'Page actions',
      ariaHaspopup: 'menu',
      testid: 'page-actions-trigger',
      onClick: (e) => this.openActionsFor(store.getState()?.activePage, e.currentTarget)
    });
    this._container.append(this._strip, this._collapsed, this._actionsBtn, this._proxy);
  }

  _listen() {
    this._offs.push(eventBus.on('state-changed', (state) => this._onState(state)));
    this._offs.push(
      eventBus.on('autosave:saved', () => {
        this._dirtyPageIds.clear();
        this._renderTabs(store.getState());
      })
    );
    this._offs.push(
      eventBus.on('toolbar:focus-pages', () => {
        (
          this._strip.querySelector('[aria-current="page"]') ||
          this._strip.querySelector('.pb-cb-ptab')
        )?.focus();
      })
    );
    this._offs.push(eventBus.on('writability-changed', () => this._syncActionsTrigger()));
    this._offs.push(
      eventBus.on('cmd:rename-current-page', () => {
        const page = store.getActivePage();
        if (page) this.renamePage(page.id);
      })
    );
    // BC-9: the palette's "Reorder pages" opened nothing visible — it focused a
    // tab in a strip that is hidden on phones and when collapsed. It opens the
    // page-actions menu for the current page on whichever surface is showing.
    this._offs.push(
      eventBus.on('cmd:page-actions', () => {
        const pageId = store.getState()?.activePage;
        if (!pageId) return;
        if (isPhoneViewport()) {
          eventBus.emit('mobile-bar:open-page-actions', { pageId });
          return;
        }
        this.openActionsFor(pageId, this._actionsBtn);
      })
    );

    this._strip.addEventListener('keydown', (e) => this._onStripKeydown(e));

    // Passive: the fade is presentational and must never delay a scroll.
    this._onStripScroll = () => this._syncOverflow();
    this._strip.addEventListener('scroll', this._onStripScroll, { passive: true });

    if (typeof ResizeObserver === 'function') {
      this._observer = new ResizeObserver(() => this._scheduleMeasure());
      this._observer.observe(this._container);
    }

    this._onState(store.getState());
  }

  destroy() {
    for (const off of this._offs) if (typeof off === 'function') off();
    this._offs = [];
    if (this._onStripScroll) {
      this._strip.removeEventListener('scroll', this._onStripScroll);
      this._onStripScroll = null;
    }
    this._observer?.disconnect();
    this._observer = null;
    if (this._frame != null) {
      cancelAnimationFrame(this._frame);
      this._frame = null;
    }
  }

  // ── state ────────────────────────────────────────────────────────────────

  _onState(state) {
    if (!state) return;
    const generation = store.getEpoch() - store.getRevision();
    this._projectReplaced = generation !== this._projectGeneration;
    this._projectGeneration = generation;
    if (this._projectReplaced) {
      this._dirtyPageIds.clear();
      this._pageStateHashes.clear();
      this._hasBaseline = false;
    }
    this._syncDirty(state);
    this._renderTabs(state);
  }

  /**
   * Per-page dirty dots. Ported from `Toolbar._syncDirtyPagesFromState`: hash
   * each page and mark the ones that moved since the last baseline.
   */
  _syncDirty(state) {
    if (!Array.isArray(state.pages)) return;
    const next = new Map();
    for (const page of state.pages) {
      if (!page?.id) continue;
      let hash;
      try {
        hash = JSON.stringify(page);
      } catch {
        // A page carrying a cyclic value cannot be hashed; fall back to an
        // identity that at least changes when the name does.
        hash = `${page.id}:${page.name || ''}`;
      }
      next.set(page.id, hash);
      if (this._hasBaseline && this._pageStateHashes.get(page.id) !== hash) {
        this._dirtyPageIds.add(page.id);
      }
    }
    for (const id of this._dirtyPageIds) if (!next.has(id)) this._dirtyPageIds.delete(id);
    this._pageStateHashes = next;
    this._hasBaseline = true;
  }

  // ── rendering ────────────────────────────────────────────────────────────

  _renderTabs(state) {
    this._renderCount += 1;
    const pages = Array.isArray(state?.pages) ? state.pages : [];
    const oldTabs = [...this._strip.querySelectorAll('.pb-cb-ptab')];
    const focused = document.activeElement;
    const focusedTab = this._strip.contains(focused) ? focused?.closest?.('.pb-cb-ptab') : null;
    const replaced = this._projectReplaced;
    this._projectReplaced = false;
    let focusPageId = null;
    const nearestPageId = (index) => pages[Math.max(0, Math.min(index, pages.length - 1))]?.id;
    const session = this._renameSession;
    if (session) {
      const page = pages.find((candidate) => candidate.id === session.pageId);
      if (replaced || !page || page.name !== session.name || session.committing) {
        if (focused === session.input || session.committing) {
          focusPageId = replaced
            ? state.activePage
            : page?.id || nearestPageId(oldTabs.indexOf(session.tab));
        }
        this._endRename(session);
      }
    }
    if (replaced) {
      if (this._strip.contains(focused) || focusedTab)
        focusPageId = state.activePage || pages[0]?.id;
      this._strip.replaceChildren(this._addBtn);
    } else {
      for (const tab of oldTabs) {
        if (!pages.some((page) => page.id === tab.dataset.pageId)) {
          if (tab === focusedTab) focusPageId = nearestPageId(oldTabs.indexOf(tab));
          tab.remove();
        }
      }
    }
    const existing = new Map((replaced ? [] : oldTabs).map((tab) => [tab.dataset.pageId, tab]));
    const nextTabs = pages.map((page) => {
      const tab = existing.get(page.id) || this._makeTab(page, state.activePage);
      this._patchTab(tab, page, state.activePage);
      return tab;
    });
    // insertBefore removes focus when it moves the focused node. Leave that
    // tab stationary and move its siblings around it, from right to left.
    let anchor = this._addBtn;
    for (let index = nextTabs.length - 1; index >= 0; index--) {
      const tab = nextTabs[index];
      if (tab !== focusedTab && tab.nextSibling !== anchor) this._strip.insertBefore(tab, anchor);
      anchor = tab;
    }
    const focusTarget = nextTabs.find((tab) => tab.dataset.pageId === focusPageId);
    const retainedFocus = this._strip.contains(focused)
      ? focused?.closest?.('.pb-cb-ptab, .pb-cb-ptab-add')
      : null;
    this._setRovingTarget(
      focusTarget ||
        retainedFocus ||
        nextTabs.find((tab) => tab.dataset.pageId === state.activePage) ||
        nextTabs[0] ||
        this._addBtn
    );
    if (focusTarget) focusTarget.focus();

    // Proxy nodes are never interactive, so rebuilding these cannot disturb
    // a tab or its input. Its labels always reflect the committed page names.
    this._proxy.textContent = '';
    for (const page of pages) this._proxy.appendChild(this._makeProxyTab(page));
    this._proxy.appendChild(this._makeProxyTab({ name: '+' }));

    // With a scrolling strip the active page can start out of view — on load,
    // or after switching from the collapsed menu. Bring it back without moving
    // focus, which belongs to whatever the user was actually using.
    this._revealActiveTab();

    this._syncActionsTrigger(state);
    const active = pages.find((p) => p.id === state?.activePage);
    this._collapsed.textContent = '';
    this._collapsed.append(
      Object.assign(document.createElement('span'), {
        className: 'pb-cb-pages-collapsed-name',
        textContent: active?.name || 'Pages'
      }),
      Object.assign(document.createElement('span'), {
        className: 'pb-cb-pages-collapsed-count',
        textContent: pages.length === 1 ? '' : `${pages.length} pages`
      })
    );
    this._collapsed.setAttribute(
      'aria-label',
      `Page: ${active?.name || 'none'}. ${pages.length} pages. Choose a page`
    );

    this._scheduleMeasure();
  }

  _setRovingTarget(target) {
    for (const tab of this._strip.querySelectorAll('.pb-cb-ptab, .pb-cb-ptab-add')) {
      tab.tabIndex = tab === target ? 0 : -1;
    }
  }

  _patchTab(tab, page, activeId) {
    const active = page.id === activeId;
    tab.setAttribute('aria-selected', String(active));
    if (active) tab.setAttribute('aria-current', 'page');
    else tab.removeAttribute('aria-current');
    const renaming = tab.hasAttribute('data-renaming');
    tab.querySelector('.pb-cb-ptab-dot').hidden = renaming || !this._dirtyPageIds.has(page.id);
    if (!renaming) tab.querySelector('.pb-cb-ptab-label').textContent = page.name;
    tab.setAttribute(
      'aria-label',
      this._dirtyPageIds.has(page.id) ? `${page.name} (unsaved changes)` : page.name
    );
  }

  _makeTab(page, activeId) {
    const isActive = page.id === activeId;
    const tab = createButton({
      variant: 'bare',
      className: 'pb-cb-ptab',
      role: 'tab',
      onClick: () => eventBus.emit('toolbar:switch-page', { pageId: page.id })
    });
    tab.dataset.pageId = page.id;
    tab.tabIndex = isActive ? 0 : -1;
    tab.setAttribute('aria-selected', String(isActive));
    if (isActive) tab.setAttribute('aria-current', 'page');
    tab.draggable = true;

    const dot = document.createElement('span');
    dot.className = 'pb-cb-ptab-dot';
    dot.setAttribute('aria-hidden', 'true');
    dot.hidden = !this._dirtyPageIds.has(page.id);
    const label = document.createElement('span');
    label.className = 'pb-cb-ptab-label';
    label.textContent = page.name;
    tab.append(dot, label);
    // The dot is the ONLY signal that a page has unsaved work, and it is
    // decorative markup — so the state has to reach the accessible name too.
    tab.setAttribute(
      'aria-label',
      this._dirtyPageIds.has(page.id) ? `${page.name} (unsaved changes)` : page.name
    );

    tab.addEventListener('dblclick', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this._startRename(page.id);
    });
    tab.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      this._openContextMenu(e, page.id);
    });
    tab.addEventListener('dragstart', (e) => {
      this._dragPageId = page.id;
      tab.classList.add('pb-dragging');
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', page.id);
      }
    });
    tab.addEventListener('dragend', () => {
      this._dragPageId = null;
      tab.classList.remove('pb-dragging');
      for (const n of this._strip.querySelectorAll('.pb-drop-before, .pb-drop-after')) {
        n.classList.remove('pb-drop-before', 'pb-drop-after');
      }
    });
    tab.addEventListener('dragover', (e) => {
      if (!this._dragPageId || this._dragPageId === page.id) return;
      e.preventDefault();
      const rect = tab.getBoundingClientRect();
      const before = e.clientX < rect.left + rect.width / 2;
      tab.classList.toggle('pb-drop-before', before);
      tab.classList.toggle('pb-drop-after', !before);
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    });
    tab.addEventListener('dragleave', () => {
      tab.classList.remove('pb-drop-before', 'pb-drop-after');
    });
    tab.addEventListener('drop', (e) => {
      if (!this._dragPageId || this._dragPageId === page.id) return;
      e.preventDefault();
      const rect = tab.getBoundingClientRect();
      const before = e.clientX < rect.left + rect.width / 2;
      this._emitReorder(this._dragPageId, page.id, before ? 'before' : 'after');
    });

    return tab;
  }

  /**
   * A listener-free, id-free copy for measurement only.
   *
   * Its class is `pb-cb-ptab-ghost`, NOT `pb-cb-ptab`. The two share every
   * styling rule (a grouped selector in command-bar.css, which is what keeps
   * the measurement honest) but not their identity — otherwise every
   * `querySelectorAll('.pb-cb-ptab')` in the app quietly returns twice as many
   * tabs as exist, half of them off-screen with no page id.
   */
  _makeProxyTab(page) {
    const el = document.createElement('span');
    el.className = 'pb-cb-ptab-ghost';
    el.textContent = page.name || '';
    return el;
  }

  // ── adaptive collapse ────────────────────────────────────────────────────

  _scheduleMeasure() {
    if (this._frame != null) return;
    const raf =
      typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame.bind(window)
        : (cb) => setTimeout(cb, 0);
    this._frame = raf(() => {
      this._frame = null;
      this._measure();
    });
  }

  _measure() {
    const natural = this._proxy.getBoundingClientRect().width;
    // The actions button shares the container, so it is not width the tabs get.
    const available = this._container.clientWidth - (this._actionsBtn?.offsetWidth || 0);
    // A zero-width container means "not laid out yet" (hidden panel, jsdom).
    // Deciding from it would collapse everything permanently.
    if (available <= 0) return;
    const required = Math.min(natural, MIN_SCROLLABLE_PX);
    const next = decideMode(required, available, this._mode);
    if (next !== this._mode) {
      this._mode = next;
      this._applyMode();
    }
    this._syncOverflow();
  }

  /**
   * Scroll the active tab into view WITHOUT focusing it. `scrollIntoView` on a
   * tab would also scroll the whole editor if an ancestor could move, so the
   * arithmetic is done against the strip alone. Guarded for jsdom, where layout
   * is all zeroes and every tab would look out of view.
   */
  _revealActiveTab() {
    const strip = this._strip;
    const tab = strip.querySelector('[aria-current="page"]');
    if (!tab || typeof strip.scrollTo !== 'function') return;
    const max = strip.scrollWidth - strip.clientWidth;
    if (max <= 1) return;

    const fade = 24; // keep the tab clear of the mask, not tucked under it
    const left = tab.offsetLeft - fade;
    const right = tab.offsetLeft + tab.offsetWidth + fade;
    let next = strip.scrollLeft;
    if (left < strip.scrollLeft) next = left;
    else if (right > strip.scrollLeft + strip.clientWidth) next = right - strip.clientWidth;
    if (next === strip.scrollLeft) return;

    strip.scrollTo({ left: Math.max(0, Math.min(next, max)), behavior: 'auto' });
    this._syncOverflow();
  }

  /**
   * Drive the edge fade from actual scroll position. Purely presentational —
   * it must never change the mode, or the fade and the collapse decision would
   * feed each other.
   */
  _syncOverflow() {
    const strip = this._strip;
    if (this._mode !== 'tabs') {
      delete strip.dataset.overflow;
      return;
    }
    // Sub-pixel widths round inconsistently across browsers; 1px of slack stops
    // a fade flickering on a strip that is exactly full.
    const max = strip.scrollWidth - strip.clientWidth;
    if (max <= 1) {
      delete strip.dataset.overflow;
      return;
    }
    const atStart = strip.scrollLeft <= 1;
    const atEnd = strip.scrollLeft >= max - 1;
    strip.dataset.overflow = atStart ? 'end' : atEnd ? 'start' : 'both';
  }

  _applyMode() {
    const collapsed = this._mode === 'collapsed';
    this._strip.hidden = collapsed;
    this._collapsed.hidden = !collapsed;
    this._container.dataset.mode = this._mode;
  }

  /** For tests and the geometry gate. */
  get mode() {
    return this._mode;
  }

  // ── interactions ─────────────────────────────────────────────────────────

  _openPageList(triggerEl) {
    const state = store.getState();
    const pages = Array.isArray(state?.pages) ? state.pages : [];
    openPopoverMenu(triggerEl, {
      owner: 'command-bar-pages',
      align: 'left',
      width: 240,
      menuLabel: 'Pages',
      items: [
        ...pages.map((p) => ({
          id: p.id,
          label: this._dirtyPageIds.has(p.id) ? `${p.name} •` : p.name,
          onSelect: () => eventBus.emit('toolbar:switch-page', { pageId: p.id })
        })),
        { type: 'separator' },
        {
          id: 'add',
          label: 'New page',
          icon: icon('plus'),
          onSelect: () => eventBus.emit('toolbar:add-page')
        }
      ]
    });
  }

  /**
   * BC-9. Rename by the visible route: inline in the tab when the strip is on
   * screen, otherwise a dialog. The palette used to start an inline rename in
   * a strip that was `display: none` (a phone) or `hidden` (collapsed), which
   * put focus into an input nobody could see.
   */
  renamePage(pageId) {
    const tab = [...this._strip.children].find((el) => el.dataset?.pageId === pageId);
    const stripVisible =
      this._mode === 'tabs' && !isHiddenOrInert(this._strip) && !isHiddenOrInert(this._container);
    if (tab && stripVisible) {
      this._startRename(pageId);
      return;
    }
    const page = (store.getState()?.pages || []).find((p) => p.id === pageId);
    if (!page) return;
    void showRenamePageDialog({ name: page.name }).then((name) => {
      if (name) eventBus.emit('toolbar:rename-page', { pageId, name });
    });
  }

  /**
   * BC-9. The trigger names the page it acts on, and in a read-only tab it is
   * disabled WITH the reason: every action would be refused, and a menu of only
   * disabled rows does not open, so the explanation belongs on the control.
   */
  _syncActionsTrigger(state = store.getState()) {
    const active = (state?.pages || []).find((p) => p.id === state?.activePage);
    const base = active ? `Page actions: ${active.name}` : 'Page actions';
    const reason = store.isWritable() ? '' : readOnlyReasonFor(store.getPrimaryBlocker());
    this._actionsBtn.setAttribute('aria-label', reason ? `${base} — ${reason}` : base);
    this._actionsBtn.dataset.tooltip = reason || 'Page actions';
    if (reason) this._actionsBtn.setAttribute('aria-disabled', 'true');
    else this._actionsBtn.removeAttribute('aria-disabled');
  }

  /** BC-9. The one page-actions menu, anchored to a control or a point. */
  openActionsFor(pageId, anchorEl, anchorRect = null) {
    if (!pageId) return null;
    if (!store.isWritable()) {
      store.refuseIfReadOnly('Page actions');
      return null;
    }
    return openPageActions(pageId, {
      anchorEl: anchorRect ? null : anchorEl,
      anchorRect,
      orientation: 'horizontal',
      onRename: (id) => this.renamePage(id),
      restoreFocus: ({ menu }) => {
        if (anchorEl && menu.contains(document.activeElement) && document.body.contains(anchorEl)) {
          anchorEl.focus();
        }
      }
    });
  }

  _openContextMenu(event, pageId) {
    // BC-9: the SAME menu the visible Page actions button opens — the
    // right-click is an accelerator for it, not a second, smaller menu.
    const trigger = event.target?.closest?.('.pb-cb-ptab') || null;
    const x = Number(event.clientX || 0);
    const y = Number(event.clientY || 0);
    this.openActionsFor(pageId, trigger, {
      left: x,
      right: x,
      top: y,
      bottom: y,
      width: 0,
      height: 0
    });
  }

  _endRename(session, { focus = false } = {}) {
    if (!session || session.closed) return;
    session.closed = true;
    if (this._renameSession === session) this._renameSession = null;
    session.tab.removeAttribute('data-renaming');
    session.input.remove();
    session.tab.draggable = true;
    session.tab.querySelector('.pb-cb-ptab-label').hidden = false;
    const state = store.getState();
    const page = state?.pages?.find((candidate) => candidate.id === session.pageId);
    if (page) this._patchTab(session.tab, page, state.activePage);
    if (focus && this._strip.contains(session.tab)) {
      this._setRovingTarget(session.tab);
      session.tab.focus();
    }
  }

  _startRename(pageId) {
    const tab = [...this._strip.children].find((el) => el.dataset?.pageId === pageId);
    if (!tab) return;
    const page = (store.getState()?.pages || []).find((p) => p.id === pageId);
    if (!page) return;
    if (this._renameSession?.pageId === pageId) return;
    this._endRename(this._renameSession);

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'pb-cb-ptab-rename';
    input.value = page.name;
    input.maxLength = PAGE_RENAME_MAX_LEN;
    input.setAttribute('aria-label', 'Page name');
    const session = {
      pageId,
      name: page.name,
      tab,
      input,
      closed: false,
      committing: false,
      composing: false
    };
    this._renameSession = session;
    tab.setAttribute('data-renaming', '');
    tab.draggable = false;
    tab.querySelector('.pb-cb-ptab-label').hidden = true;
    tab.querySelector('.pb-cb-ptab-dot').hidden = true;
    tab.appendChild(input);
    this._setRovingTarget(tab);
    input.focus();
    input.select();

    const commit = () => {
      if (session.closed) return;
      const name = input.value.trim();
      if (!name || name === session.name) {
        this._endRename(session, { focus: true });
        return;
      }
      session.committing = true;
      const renders = this._renderCount;
      eventBus.emit('toolbar:rename-page', { pageId, name });
      // The shell writes synchronously. A refusal emits no state-changed and
      // therefore needs only its old label restored, not another strip render.
      if (this._renderCount === renders) this._endRename(session, { focus: true });
    };
    input.addEventListener('blur', () => {
      if (!session.committing) this._endRename(session);
    });
    input.addEventListener('compositionstart', () => {
      session.composing = true;
    });
    input.addEventListener('compositionend', () => {
      session.composing = false;
    });
    input.addEventListener('keydown', (e) => {
      if (session.composing || isComposing(e)) return;
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        commit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        this._endRename(session, { focus: true });
      }
    });
  }

  _onStripKeydown(e) {
    if (e.target?.closest?.('.pb-cb-ptab-rename') || isComposing(e)) return;
    const focused = document.activeElement?.closest('.pb-cb-ptab, .pb-cb-ptab-add');

    if ((e.shiftKey && e.key === 'F10') || e.key === 'ContextMenu') {
      if (!focused || !this._strip.contains(focused) || !focused.dataset.pageId) return;
      e.preventDefault();
      const rect = focused.getBoundingClientRect();
      this._openContextMenu(
        {
          target: focused,
          clientX: rect.left + Math.min(24, Math.max(rect.width - 8, 8)),
          clientY: rect.bottom + 4
        },
        focused.dataset.pageId
      );
      return;
    }

    // Neither rename nor reorder means anything on the `+`, which carries no
    // page id — `undefined` would have been passed straight into a rename.
    if (focused && !focused.dataset.pageId) {
      if (e.key === 'F2' || (e.altKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight'))) return;
    }

    if (focused && e.key === 'F2') {
      e.preventDefault();
      e.stopPropagation();
      this._startRename(focused.dataset.pageId);
      return;
    }

    if (e.altKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
      if (!focused) return;
      e.preventDefault();
      this._reorderByKeyboard(focused.dataset.pageId, e.key === 'ArrowLeft' ? -1 : 1);
      return;
    }

    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
    // CMD-8: the `+` is part of the strip, so it is part of the rotation. It
    // used to be excluded, which left it permanently `tabindex="0"` — a SECOND
    // Tab stop inside a widget the composite focus model treats as one, and the
    // only way to reach it was the Tab press the model exists to save.
    const tabs = [...this._strip.querySelectorAll('.pb-cb-ptab, .pb-cb-ptab-add')];
    if (!tabs.length) return;
    const idx = tabs.indexOf(focused);
    if (idx === -1) return;
    e.preventDefault();
    let next;
    if (e.key === 'ArrowRight') next = (idx + 1) % tabs.length;
    else if (e.key === 'ArrowLeft') next = (idx - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') next = 0;
    else next = tabs.length - 1;
    tabs[idx].tabIndex = -1;
    tabs[next].tabIndex = 0;
    tabs[next].focus();
  }

  _emitReorder(draggedId, targetId, position) {
    const pages = store.getState()?.pages || [];
    const ids = pages.map((p) => p.id);
    const from = ids.indexOf(draggedId);
    const target = ids.indexOf(targetId);
    if (from === -1 || target === -1 || from === target) return;
    ids.splice(from, 1);
    let at = ids.indexOf(targetId);
    if (position === 'after') at += 1;
    ids.splice(at, 0, draggedId);
    if (ids.every((id, i) => id === pages[i]?.id)) return;
    eventBus.emit('toolbar:reorder-pages', { orderedIds: ids });
  }

  _reorderByKeyboard(pageId, direction) {
    const pages = store.getState()?.pages || [];
    const ids = movePageId(
      pages.map((page) => page.id),
      pageId,
      direction
    );
    if (!ids) {
      if (pages.some((page) => page.id === pageId)) {
        showToast(direction < 0 ? 'Already first' : 'Already last', 'info', {
          id: 'page-reorder-boundary'
        });
      }
      return;
    }
    eventBus.emit('toolbar:reorder-pages', { orderedIds: ids });
  }
}
